import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { toast } from "sonner";
import { Tv, Copy, CheckCircle, Loader2, ChevronLeft } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ActivationPage = () => {
  const navigate = useNavigate();
  const [deviceKey, setDeviceKey] = useState("");
  const [activationCode, setActivationCode] = useState("");
  const [deviceStatus, setDeviceStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activating, setActivating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Check for existing device key in localStorage
  useEffect(() => {
    const checkDevice = async () => {
      const storedKey = localStorage.getItem("deviceKey");
      
      if (storedKey) {
        try {
          const response = await axios.get(`${API}/device/status/${storedKey}`);
          setDeviceKey(storedKey);
          setDeviceStatus(response.data);
          
          // If device is active or in trial, redirect to menu
          if (response.data.status === "active" || response.data.status === "trial") {
            navigate("/menu");
            return;
          }
        } catch (error) {
          // Device not found, register new one
          await registerDevice();
        }
      } else {
        await registerDevice();
      }
      setLoading(false);
    };

    checkDevice();
  }, [navigate]);

  const registerDevice = async () => {
    try {
      const response = await axios.post(`${API}/device/register`, {
        device_name: navigator.userAgent.substring(0, 50),
        device_type: "web"
      });
      
      setDeviceKey(response.data.device_key);
      setDeviceStatus(response.data);
      localStorage.setItem("deviceKey", response.data.device_key);
    } catch (error) {
      toast.error("Failed to register device");
      console.error(error);
    }
  };

  const handleActivate = async () => {
    if (!activationCode || activationCode.length !== 6) {
      toast.error("Please enter a valid 6-digit activation code");
      return;
    }

    setActivating(true);
    try {
      const response = await axios.post(`${API}/device/activate`, {
        device_key: deviceKey,
        activation_code: activationCode
      });
      
      setDeviceStatus(response.data);
      toast.success("Device activated successfully!");
      
      // Redirect to menu
      setTimeout(() => {
        navigate("/menu");
      }, 1000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid activation code");
    } finally {
      setActivating(false);
    }
  };

  const copyDeviceKey = () => {
    navigator.clipboard.writeText(deviceKey);
    setCopied(true);
    toast.success("Device key copied to clipboard");
    setTimeout(() => setCopied(false), 2000);
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  if (loading) {
    return (
      <div className="min-h-screen nettv-bg flex items-center justify-center">
        <div className="text-center relative z-10">
          <div className="w-24 h-24 rounded-full bg-yellow-400 flex items-center justify-center mx-auto mb-6 animate-spin">
            <div className="w-20 h-20 rounded-full bg-[#0a1628] flex items-center justify-center">
              <Tv className="w-8 h-8 text-yellow-400" />
            </div>
          </div>
          <p className="text-white text-lg font-medium">JUST A SEC...</p>
          <p className="text-white/60 text-sm">We're on it!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen nettv-bg text-white">
      {/* Header */}
      <header className="flex items-center justify-between px-6 py-4 bg-black/30 relative z-10">
        <div className="nettv-logo">
          <img src="/nettv-logo.png" alt="NetTV" className="h-8" />
        </div>
        
        <div className="flex items-center gap-6 text-sm">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-1 text-white/60 hover:text-white"
          >
            <ChevronLeft className="w-4 h-4" /> Back
          </button>
          <div className="text-white/80">
            {formatTime(currentTime)} {formatDate(currentTime)}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex items-center justify-center min-h-[calc(100vh-160px)] px-6 relative z-10">
        <div className="w-full max-w-lg">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="nettv-logo inline-block mb-4">
              <img src="/nettv-logo.png" alt="NetTV" className="h-16 mx-auto" />
            </div>
            <p className="text-white/60">Device Activation</p>
          </div>

          {/* Device Key Display */}
          <div className="bg-black/30 rounded-xl p-6 mb-6">
            <p className="text-sm text-white/60 mb-2 text-center">Your Device ID</p>
            <div 
              className="flex items-center justify-center gap-3 cursor-pointer group"
              onClick={copyDeviceKey}
              data-testid="device-key-container"
            >
              <span className="font-mono text-3xl font-bold tracking-[0.2em] text-cyan-400" data-testid="device-key-display">
                {deviceKey}
              </span>
              {copied ? (
                <CheckCircle className="w-5 h-5 text-green-400" />
              ) : (
                <Copy className="w-5 h-5 text-white/40 group-hover:text-white transition-colors" />
              )}
            </div>
            <p className="text-xs text-white/40 text-center mt-2">Click to copy • Share with your provider</p>
          </div>

          {/* Status Badge */}
          {deviceStatus && (
            <div className="flex justify-center mb-6">
              <span className={`px-4 py-1.5 rounded-full text-sm font-medium ${
                deviceStatus.status === "pending" ? "bg-white/10 text-white/60" :
                deviceStatus.status === "trial" ? "bg-yellow-400/20 text-yellow-400" :
                deviceStatus.status === "active" ? "bg-green-400/20 text-green-400" :
                "bg-red-400/20 text-red-400"
              }`} data-testid="device-status-badge">
                {deviceStatus.status === "pending" && "Awaiting Activation"}
                {deviceStatus.status === "trial" && `Trial: ${deviceStatus.days_remaining} days left`}
                {deviceStatus.status === "active" && "Active - LIFETIME"}
                {deviceStatus.status === "expired" && "Expired"}
              </span>
            </div>
          )}

          {/* Activation Code Input */}
          <div className="bg-black/30 rounded-xl p-6">
            <label className="text-sm text-white/60 block mb-3 text-center">Enter Activation Code</label>
            <Input
              type="text"
              maxLength={6}
              value={activationCode}
              onChange={(e) => setActivationCode(e.target.value.replace(/\D/g, ""))}
              placeholder="000000"
              className="h-14 text-center text-2xl tracking-[0.5em] font-mono bg-black/50 border-white/20 text-white placeholder:text-white/20"
              data-testid="activation-code-input"
            />
            
            <Button
              onClick={handleActivate}
              disabled={activating || activationCode.length !== 6}
              className="w-full h-12 mt-4 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-black font-medium text-lg"
              data-testid="activate-button"
            >
              {activating ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Activating...
                </>
              ) : (
                "OK"
              )}
            </Button>
          </div>

          {/* Trial Info */}
          <p className="text-center text-xs text-white/40 mt-4">
            7-day free trial • Contact your provider for activation
          </p>

          {/* Already activated link */}
          {(deviceStatus?.status === "trial" || deviceStatus?.status === "active") && (
            <Button
              variant="ghost"
              onClick={() => navigate("/menu")}
              className="w-full mt-4 text-cyan-400 hover:text-cyan-300"
              data-testid="go-to-player-button"
            >
              Continue to NetTV →
            </Button>
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="fixed bottom-0 left-0 right-0 px-6 py-3 bg-black/50 text-center text-sm text-white/40 relative z-10">
        <p>NetTV.app • Premium IPTV Player</p>
      </footer>
    </div>
  );
};

export default ActivationPage;
